export type ValueOrderType = "Reva" | "Inspection" | "DocumentAcquisition" | "Other";

/** Matches a REVA-ID (also covers a snapshot id "REVA-….<n>" or "REVA-…:<n>"). */
export function isRevaIdFormat(value: string): boolean {
    return /^REVA-\S+$/.test(value);
}

/** Matches an externally issued order number of the form "HS-<digits>-<...>-<digits>". */
export function isExternalOrderNumberFormat(value: string): boolean {
    return /^HS-\d+-\w+-\d+$/.test(value);
}

/**
 * Details of a ValueAG order, as the Orders-API delivers them and the backend passes them through untyped
 * (`ValuationDataOrderApiModel.orderDetails` is an `ObjectNode`). Every field is therefore optional — the shape
 * below is what we have observed, not a contract the backend enforces.
 */
export interface ValueOrderClientInfo {
    name?: string;
    department?: string;
    abbreviation?: string;
}

export interface ValueOrderClerk {
    id?: string;
    firstName?: string;
    surname?: string;
    email?: string;
    phoneBusiness?: string;
    phoneMobile?: string;
}

export interface ValueOrderServiceLevel {
    id?: number;
    days?: number;
}

export interface ValueOrderAddressInfo {
    street?: string;
    houseNumber?: string;
    zipCode?: string;
    city?: string;
    district?: string;
    state?: string;
    isDevelopmentArea?: boolean;
    addressAddition?: string;
    /** #18967: plain-string reference address, set when the real address is not georeferenceable. */
    referenceAddress?: string;
}

export interface ValueOrderProgress {
    code?: string;
    name?: string;
    percent?: number;
}

export interface ValueOrderDetails {
    externalOrderNumber?: string;
    clientOrderReference?: string;
    clientInformation?: ValueOrderClientInfo;
    globalId?: string;
    orderType?: string;
    orderOptions?: string[];
    serviceLevel?: ValueOrderServiceLevel;
    addressInformation?: ValueOrderAddressInfo;
    creationDate?: string;
    deadline?: string;
    inspectionDate?: string;
    completionDate?: string;
    processingCompletedDate?: string;
    cancellationDate?: string;
    processingStatus?: ValueOrderProgress;
    creator?: string;
    clerk?: ValueOrderClerk;
    information?: string;
    propertyTypeAddition?: string;
    isSmallMortgageValuation?: boolean;
}

/**
 * A ValueAG order attached to a valuation (`ValuationDataOrderApiModel`). The list on a valuation is pre-sorted by
 * precedence, most relevant first.
 */
export interface ValueOrderData {
    orderType: ValueOrderType;
    valueExternalOrderNumber: string;
    valueOrderGuid: string;
    valueClientGuid: string;
    /** The only nullable field of an order — georeferencing the order address can fail. */
    orderAddress?: string;
    orderDetails: ValueOrderDetails;
}

/**
 * A document stored on a value order. The same record wherever REVA deals with documents: the list of a
 * valuation (see {@link ValueOrderDocumentList}), the documents a (pre-)finalization archived, and the
 * list reva-pdf renders into a report.
 */
export interface ValueOrderDocument {
    /** Id of the document inside its value order; with {@link valueOrderGuid} it addresses the download. */
    documentId: string;
    /** Guid of the value order the document belongs to. */
    valueOrderGuid: string;
    /** Document type as the Orders-API names it, e.g. "Wertgutachten aller Art" or "Bild" — free text. */
    documentType: string;
    /** File name including the extension, e.g. "2026-10-13_HS-26-…_Gutachten.pdf". */
    filename: string;
    /** File extension without the dot, e.g. "pdf"; part of the download URL. */
    extension: string;
    /**
     * When the document was created, as a local date-time without a UTC offset
     * (e.g. `2026-10-13T14:11:30.733136`) — to be read as Europe/Berlin.
     */
    creationDate: string;
}

/** Response of listing the documents of a valuation (`GET /valuations/{id}/documents`). */
export interface ValueOrderDocumentList {
    items: ValueOrderDocument[];
}

/** Auftragsart, die REVA bei der ValueAG beauftragen kann (`CommissionValueOrderRequest.orderType` in reva-rest). */
export type CommissionOrderType = "ExteriorInspection" | "InteriorInspection" | "DocumentCollection";

/** Alle beauftragbaren Auftragsarten in der Reihenfolge, in der der Beauftragungsdialog sie anbietet. */
export const COMMISSION_ORDER_TYPES: readonly CommissionOrderType[] = [
    "ExteriorInspection",
    "InteriorInspection",
    "DocumentCollection",
];

/**
 * Ergebnis der Rechteprüfung beim ValueAG-Auftraggeber (`GET /value-orders/entitlements`):
 * - `OK`: Konfiguration gelesen, `orderTypes`/`propertyTypes` sind aussagekräftig.
 * - `NO_CLIENT_ID`: Lizenz ohne Value-Client-ID.
 * - `CLIENT_NOT_PERMITTED`: Die Orders-API lehnt den Auftraggeber für REVA ab (403, bzw. 401 nur für ihn laut Gegenprobe in reva-rest).
 * - `UNAVAILABLE`: Orders-/Auth-API nicht erreichbar, nicht konfiguriert oder Token abgelehnt – Beauftragen ist gerade
 *   nicht möglich (Button ausgegraut).
 * - `UNRECOGNIZED`: Orders-API hat geantwortet, reva-rest kennt aber keinen ihrer Auftragsart-Texte – Lücke in der
 *   Zuordnung, Beauftragen bleibt ohne Einschränkung möglich.
 */
export type ValueOrderEntitlementsStatus =
    | "OK"
    | "NO_CLIENT_ID"
    | "CLIENT_NOT_PERMITTED"
    | "UNAVAILABLE"
    | "UNRECOGNIZED";

/** Freischaltung einer REVA-Auftragsart beim Auftraggeber. */
export interface ValueOrderEntitlementOrderType {
    /** Die drei Beauftragungs-Arten, dazu `Reva` (der REVA-Auftrag selbst, nicht über den Dialog beauftragbar). */
    orderType: CommissionOrderType | "Reva";
    /** Name der Auftragsart in der Orders-API, z. B. `OutsideInspection`. */
    valueOrderType: string;
    allowed: boolean;
    /** Freigeschaltete Optionen dieser Auftragsart, so wie die Orders-API sie benennt. */
    orderOptions: string[];
    /**
     * Flag-Werte (`key`) der freigeschalteten Optionen in der Orders-API, je Option ein Eintrag (z. B. `[4, 8]`).
     * Ältere reva-rest-Stände liefern das Feld nicht – dann filtert REVA die Optionen nicht.
     */
    orderOptionKeys?: number[];
}

/** Eine Zeile aus `GET /Client/configuration/order-types` der Orders-API, unverändert durchgereicht. */
export interface ValueOrderRawOrderType {
    id: number;
    value?: string;
    orderOptions: {key: number; value?: string}[];
    propertyTypes: {id: number; name?: string; group?: string}[];
    serviceLevels: {key: number; value: number}[];
}

/**
 * Antwort von reva-rest `GET /value-orders/entitlements` (`ValueOrderEntitlementsModel`).
 * reva-rest lässt `null`-Felder weg (`ObjectMapperContextResolver`), daher sind die nullbaren Felder optional.
 */
export interface ValueOrderEntitlementsModel {
    status: ValueOrderEntitlementsStatus;
    valueClientGuid?: string | null;
    /** ISO-8601-Zeitpunkt der Abfrage bei der Orders-API (Cache-Stand). */
    checkedAt?: string | null;
    /** Immer alle drei REVA-Auftragsarten; bei `status !== "OK"` alle mit `allowed=false`. */
    orderTypes: ValueOrderEntitlementOrderType[];
    /**
     * Erlaubte REVA-Gebäudearten (Namen wie `EFH_ZFH`, `Condominium`); `null`, wenn reva-rest keinen der Gebäudeart-Namen
     * der Orders-API kennt – dann entfällt die Gebäudeart-Prüfung.
     */
    propertyTypes: string[] | null;
    rawOrderTypes: ValueOrderRawOrderType[];
}
